import type { GetProductOffersResult, PlayerProductOffer, Service } from '@/types/schemas';
import type { PassengerInfo } from '@/types/schemas/player/common';
import { getRoomTypeByCode, type NBCSingleProductResponse } from '@/types/schemas/nbc';
import type { RoomOccupancy } from '@/types/SearchBar';
import type { RoomDetails } from '@/types/offer';
import { daysBetween } from '@/utils/date/saturdayWindow';
import { boardLabel } from '@/utils/formatting/boardLabel';

/**
 * Mapped offer - internal representation of a priced travel offer
 *
 * This interface represents Player API offers in a UI-friendly format.
 * It abstracts Player API terminology and provides clear field names.
 *
 * TERMINOLOGY:
 * - offerId: Unique offer ID from Player API (Product.Id)
 * - serviceId: Player-specific service reference (not related to DaVinci's "Service")
 * - serviceVariantId: Player-specific variant reference (date range + room/board config)
 *
 * @see PlayerProductOffer for the source API type
 */
export interface MappedOffer {
  // Offer identification
  /**
   * Unique offer ID from Player API (Product.Id)
   * This is the numeric offer identifier, NOT the product code (GIATA ID).
   * The product code (GIATA ID) is found in Service.AccommodationSpecific.ProductCode.
   */
  offerId: number;

  /**
   * Player API service ID
   * Internal reference used by Player for accommodation data.
   * NOT related to DaVinci's "Service" field (which holds product code).
   */
  serviceId: number;

  /**
   * Player API service variant ID
   * References specific date range + room/board configuration
   */
  serviceVariantId: number;

  // Date info
  startDate: string;
  endDate: string;

  // Room info
  roomCode: string;
  roomDescription: string;
  roomTypes: string[];

  // Board info
  boardCode: string;
  boardType: string;
  boardName: string;

  // Price info
  price: number;
  pricePerPerson: boolean;
  currency: string;

  // Discount info
  discount?: number;
  discountReason?: string;
  oldPrice?: number;

  // Booking info
  requestBooking: boolean;
  availability: 'unknown' | 'available' | 'unavailable';

  /**
   * Passenger IDs assigned to this offer (identifies which room)
   * Empty array = single-room booking or unknown assignment
   * @example [1, 2] - Room with passengers 1 and 2
   */
  passengerIds: number[]; // Required, empty array if unknown

  /**
   * Detailed passenger info for this room (type, age)
   * Includes all passenger details needed for DaVinci availability check
   * @example [{ id: 1, type: 'adult' }, { id: 2, type: 'child', age: 10 }]
   */
  passengers: PassengerInfo[]; // Required, populated from roomOccupancy
}

/**
 * Room combination for multi-room bookings
 * Represents a bookable combination of individual room products with unified meal plan
 * Also used for single-room (with products array of length 1) for consistency
 */
export interface RoomCombination {
  /**
   * Array of products making up this combination (one per room)
   * Single-room: array length = 1
   * Multi-room: array length = number of rooms
   * @example [Product15, Product1] - Room 1 product + Room 2 product
   */
  products: MappedOffer[];

  /**
   * Total price for all rooms combined
   * Calculated as sum of individual product prices
   */
  totalPrice: number;

  /**
   * Currency code (same for all products)
   */
  currency: string;

  /**
   * Unified meal plan code (same across all rooms)
   * @example "F" for Breakfast
   */
  boardCode: string;

  /**
   * Unified meal plan type
   * @example "BB" for Bed & Breakfast
   */
  boardType: string;

  /**
   * Unified meal plan name
   * @example "Frühstück"
   */
  boardName: string;

  /**
   * Date range (same for all rooms)
   */
  startDate: string;
  endDate: string;

  /**
   * Room descriptions for display
   * @example ["Beach Suite", "Beach Room"]
   */
  roomDescriptions: string[];

  /**
   * Room codes for display
   * @example ["D03", "D01"]
   */
  roomCodes: string[];

  /**
   * Passenger count per room for display
   * @example [3, 2] - Room 1 has 3 passengers, Room 2 has 2
   */
  passengerCounts: number[];

  /**
   * Availability status (based on all products)
   */
  availability: 'unknown' | 'available' | 'unavailable';

  /**
   * Discount information (if any product has discount)
   */
  discount?: number;
  discountReason?: string;
  oldPrice?: number;

  /**
   * All passengers across all rooms in this combination
   * Used for creating DaVinci availability check requests
   * @example [
   *   { id: 1, type: 'adult' },
   *   { id: 2, type: 'adult' },
   *   { id: 3, type: 'adult' },
   *   { id: 4, type: 'adult' },
   *   { id: 5, type: 'child', age: 4 }
   * ]
   */
  allPassengers: PassengerInfo[]; // Required
}

/**
 * Group of room combinations with same room types but different meal plans
 * Used for displaying one card with expandable meal options
 * Works for both single-room and multi-room scenarios
 */
export interface RoomCombinationGroup {
  /**
   * Key identifier for grouping (date + room codes)
   * @example "2026-11-02|2026-11-09|D03,D01"
   */
  groupKey: string;

  /**
   * Date range for this group
   */
  startDate: string;
  endDate: string;

  /**
   * Room descriptions
   */
  roomDescriptions: string[];

  /**
   * Room codes
   */
  roomCodes: string[];

  /**
   * Passenger counts per room
   */
  passengerCounts: number[];

  /**
   * Cheapest combination (displayed as main option)
   */
  cheapestCombination: RoomCombination;

  /**
   * Other combinations with different meal plans (displayed in accordion)
   */
  otherCombinations: RoomCombination[];

  /**
   * Total number of rooms in this combination
   */
  totalRooms: number;

  /**
   * Is this a multi-room combination?
   * Single-room: false
   * Multi-room: true
   */
  isMultiRoom: boolean;

  /**
   * Availability status
   */
  availability: 'unknown' | 'available' | 'unavailable';

  /**
   * Number of fixed-bookable combinations (no requestBooking product) in this group.
   * Groups are sorted by this count descending, then by cheapest price.
   */
  fixedBookingCount: number;

  /**
   * Length of this group's stay in nights, or `undefined` when the dates cannot
   * be read. Derived rather than reported by the API, and used for ordering: a
   * group whose length is unknown sorts last instead of poisoning the comparison.
   */
  nights?: number;

  /**
   * Per-room NBC enrichment for unified display (single and multi-room)
   * UNIFIED FLOW: Always present for both single-room and multi-room
   * Single-room: array with 1 element
   * Multi-room: array with N elements
   * Contains room details with NBC data (images, descriptions, room type names)
   */
  roomDetails?: RoomDetails[];
}

/**
 * Map Player API offers to internal MappedOffer format
 * Enhanced with multi-room support and passenger details
 *
 * TERMINOLOGY TRANSLATION:
 * - Player "Product" (from GetProductOffers) → MappedOffer
 * - Player "Product.Id" (numeric) → MappedOffer.productId (will be renamed to offerId)
 * - Player "Service" (container) → serviceId + serviceVariantId references
 * - Player "AccommodationSpecific.ProductCode" → extracted to get GIATA ID separately
 *
 * This mapper abstracts Player API's terminology from our internal representation,
 * providing a consistent, UI-friendly interface for working with offers.
 *
 * @param result - GetProductOffers API response from Player
 * @param roomOccupancy - Room occupancy from search params (required for passenger details)
 * @returns Array of offers in UI-friendly MappedOffer format with passenger details
 *
 * @see MappedOffer for the output interface
 * @see PlayerProductOffer for the input API type
 * @see /VueApp/TERMINOLOGY.md for complete terminology mapping
 */
export function mapOffersFromApi(
  result: GetProductOffersResult | null,
  roomOccupancy: RoomOccupancy
): MappedOffer[] {
  if (!result || !result.Products?.Product || !result.Services?.Service) {
    return [];
  }

  // Convert room occupancy to passenger details
  const allPassengers = convertRoomOccupancyToPassengers(roomOccupancy);

  const products = Array.isArray(result.Products.Product)
    ? result.Products.Product
    : [result.Products.Product];

  const services = Array.isArray(result.Services.Service)
    ? result.Services.Service
    : [result.Services.Service];

  const mappedOffers: MappedOffer[] = [];

  for (const product of products) {
    const mapped = mapSingleProduct(product, services, result.Currency || 'EUR', allPassengers);
    if (mapped) {
      mappedOffers.push(mapped);
    }
  }

  return mappedOffers;
}

/**
 * Map a single Player Product (offer) to MappedOffer format
 *
 * @param product - Player Product from GetProductOffers (represents an offer)
 * @param services - Array of Service objects from the API response
 * @param currency - Currency code (e.g., 'EUR')
 * @param allPassengers - All passengers with details (for passenger matching)
 * @returns Mapped offer or null if mapping fails
 */
function mapSingleProduct(
  product: PlayerProductOffer,
  services: Service[],
  currency: string,
  allPassengers: PassengerInfo[]
): MappedOffer | null {
  const serviceRef = product.ServiceReferences.ServiceReference;
  const firstRef = Array.isArray(serviceRef) ? serviceRef[0] : serviceRef;

  const serviceId = firstRef.ServiceId;
  const serviceVariantId = firstRef.ServiceVariantId;
  const passengerIds = firstRef.PassengerIds?.PassengerId || [];

  // Capture the hotel's Adult/Child/Infant classification per passenger id, from the
  // offer's price breakdown (Player echoes the global passenger ids in PriceInformation).
  const passengerClassMap = buildPassengerClassMap(product);

  // Get passenger details for this room.
  // Single-room fix: Player omits PassengerIds for single-room responses, so passengerIds
  // is []. Populate the details from allPassengers (every passenger belongs to the one
  // room) — otherwise checkout receives an empty participant list and classification can't
  // flow. (passengerIds itself stays [] until canonicalizeRoomOccupancy assigns ids.)
  const roomPassengers =
    passengerIds.length > 0 && allPassengers.length > 0
      ? getPassengerDetails(passengerIds, allPassengers)
      : allPassengers;

  const passengers = enrichPassengersWithClassification(roomPassengers, passengerClassMap);

  const service = services.find((s) => s.Id === serviceId);
  if (!service) {
    console.warn(`Service ${serviceId} not found for product ${product.Id}`);
    return null;
  }

  const variants = service.AccommodationService.AccommodationVariants.AccommodationVariant;
  const variantsArray = Array.isArray(variants) ? variants : [variants];

  const variant = variantsArray.find((v) => v.Id === serviceVariantId);
  if (!variant) {
    console.warn(`Variant ${serviceVariantId} not found for product ${product.Id}`);
    return null;
  }

  const room = variant.Rooms?.Room;
  if (!room) {
    console.warn(`Room data missing for variant ${serviceVariantId} in product ${product.Id}`);
    return null;
  }
  const roomObj = Array.isArray(room) ? room[0] : room;

  if (!roomObj) {
    console.warn(`Room object is empty for variant ${serviceVariantId} in product ${product.Id}`);
    return null;
  }

  // RoomTypes may be missing from API response
  let roomTypes: string[] = [];
  if (roomObj.RoomTypes?.RoomType) {
    roomTypes = Array.isArray(roomObj.RoomTypes.RoomType)
      ? roomObj.RoomTypes.RoomType
      : [roomObj.RoomTypes.RoomType];
  }

  const board = variant.Boards?.Board;
  if (!board) {
    console.warn(`Board data missing for variant ${serviceVariantId} in product ${product.Id}`);
    return null;
  }
  const boardObj = Array.isArray(board) ? board[0] : board;

  if (!boardObj) {
    console.warn(`Board object is empty for variant ${serviceVariantId} in product ${product.Id}`);
    return null;
  }

  // Drop offers missing the room booking code (required for the DaVinci Accommodation field).
  // A missing board <Code> is NOT an error: it means self-catering / no meal
  // (e.g. apartments). Such offers are kept with boardCode = '' (see return below).
  if (!roomObj.Code) {
    console.warn(
      `Dropping offer for product ${product.Id} (variant ${serviceVariantId}): ` +
        `missing room booking code`
    );
    return null;
  }

  // Normalize PriceType and PriceDetail arrays (XML conversion inconsistency)
  const priceTypesRaw = product.PriceInformation?.PriceTypes?.PriceType;
  const priceTypes = priceTypesRaw
    ? Array.isArray(priceTypesRaw)
      ? priceTypesRaw
      : [priceTypesRaw]
    : [];

  const priceDetailsRaw = product.PriceInformation?.PriceDetails?.PriceDetail;
  const priceDetails = priceDetailsRaw
    ? Array.isArray(priceDetailsRaw)
      ? priceDetailsRaw
      : [priceDetailsRaw]
    : [];

  let discount: number | undefined;
  let discountReason: string | undefined;
  let oldPrice: number | undefined;

  if (priceTypes.length > 0) {
    discountReason = priceTypes[0];
  }

  if (priceDetails.length > 0) {
    const totalDiscount = priceDetails.reduce((sum, detail) => sum + detail.Amount, 0);
    oldPrice = product.PriceInformation.Amount - totalDiscount;

    if (oldPrice > 0) {
      discount = Math.round((Math.abs(totalDiscount) / oldPrice) * 100);
    }
  }

  return {
    offerId: product.Id,
    serviceId,
    serviceVariantId,
    startDate: variant.Start,
    endDate: variant.End,
    roomCode: roomObj.Code,
    roomDescription: roomObj.Description || 'Unknown Room',
    roomTypes,
    boardCode: boardObj.Code ?? '',
    boardType: boardObj.BoardType || 'UNKNOWN',
    boardName: boardLabel(boardObj.Name),
    price: product.PriceInformation.Amount,
    pricePerPerson: product.PriceInformation.PerPerson,
    currency,
    discount,
    discountReason,
    oldPrice,
    requestBooking: product.RequestBooking || false,
    availability: 'unknown',
    passengerIds, // Required (empty array if none)
    passengers, // Required (empty array if none)
  };
}

/**
 * Get cheapest offer from mapped offers
 */
export function getCheapestOffer(offers: MappedOffer[]): MappedOffer | null {
  if (offers.length === 0) return null;

  return offers.reduce((cheapest, current) =>
    current.price < cheapest.price ? current : cheapest
  );
}

/**
 * Format date range for display ("15.11. - 22.11.2025")
 */
export function formatDateRange(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const startDay = start.getDate();
  const startMonth = start.getMonth() + 1;

  const endDay = end.getDate();
  const endMonth = end.getMonth() + 1;
  const endYear = end.getFullYear();

  return `${startDay}.${startMonth}. - ${endDay}.${endMonth}.${endYear}`;
}

/**
 * Enrich room combination groups with NBC room type data
 *
 * Always creates roomDetails array with NBC data (if available) for all rooms
 * Single-room: roomDetails array with 1 element
 * Multi-room: roomDetails array with N elements
 *
 * @param groups - Array of RoomCombinationGroup to enrich
 * @param nbcData - NBC product response with room type data
 * @returns Array of enriched RoomCombinationGroup objects with roomDetails
 *
 * @example Single-room enrichment (unified flow)
 * enrichRoomCombinationGroupsWithNbc(
 *   [{ roomCodes: ['D03'], isMultiRoom: false, ... }],
 *   nbcData
 * )
 * // Returns: [{
 * //   roomCodes: ['D03'],
 * //   roomDetails: [
 * //     { roomCode: 'D03', roomDescription: 'Beach Suite', passengerCount: 2, nbc: { images: [...], ... } }
 * //   ],
 * //   ...
 * // }]
 *
 * @example Multi-room enrichment (unified flow)
 * enrichRoomCombinationGroupsWithNbc(
 *   [{ roomCodes: ['D03', 'D01'], isMultiRoom: true, roomDescriptions: ['Beach Suite', 'Beach Room'], passengerCounts: [3, 2], ... }],
 *   nbcData
 * )
 * // Returns: [{
 * //   roomCodes: ['D03', 'D01'],
 * //   roomDetails: [
 * //     { roomCode: 'D03', roomDescription: 'Beach Suite', passengerCount: 3, nbc: { ... } },
 * //     { roomCode: 'D01', roomDescription: 'Beach Room', passengerCount: 2, nbc: { ... } }
 * //   ],
 * //   ...
 * // }]
 */
export function enrichRoomCombinationGroupsWithNbc(
  groups: RoomCombinationGroup[],
  nbcData: NBCSingleProductResponse | null
): RoomCombinationGroup[] {
  // Always process groups to ensure roomDetails is created even when NBC data is unavailable
  const hasNbcData = nbcData && nbcData.roomTypes.length > 0;

  return groups.map((group) => {
    const roomDetails = group.roomCodes.map((roomCode, index) => {
      const nbcRoomType = hasNbcData ? getRoomTypeByCode(nbcData!, roomCode) : null;

      return {
        roomCode,
        roomDescription: group.roomDescriptions[index],
        passengerCount: group.passengerCounts[index],
        nbc: nbcRoomType
          ? {
              images: nbcRoomType.roomTypeImages,
              description: nbcRoomType.roomTypeDescription,
              roomTypeName: nbcRoomType.roomTypeName,
            }
          : undefined,
      };
    });

    return {
      ...group,
      roomDetails,
    };
  });
}

// ----- Passenger Details Helpers -----

/**
 * Convert RoomOccupancy to flat array of PassengerInfo with IDs
 * Assigns sequential IDs starting from 1
 *
 * @param roomOccupancy - Room occupancy from URL parameters
 * @returns Array of passengers with IDs, types, and ages
 *
 * @example
 * convertRoomOccupancyToPassengers({
 *   rooms: [
 *     { persons: [{ type: 'adult' }, { type: 'adult' }, { type: 'adult' }] },
 *     { persons: [{ type: 'adult' }, { type: 'child', age: 4 }] }
 *   ]
 * })
 * // Returns: [
 * //   { id: 1, type: 'adult' },
 * //   { id: 2, type: 'adult' },
 * //   { id: 3, type: 'adult' },
 * //   { id: 4, type: 'adult' },
 * //   { id: 5, type: 'child', age: 4 }
 * // ]
 */
export function convertRoomOccupancyToPassengers(roomOccupancy: RoomOccupancy): PassengerInfo[] {
  const passengers: PassengerInfo[] = [];
  let passengerId = 1;

  for (const room of roomOccupancy.rooms) {
    for (const person of room.persons) {
      passengers.push({
        id: passengerId++,
        type: person.type,
        age: person.age,
      });
    }
  }

  return passengers;
}

/**
 * Get PassengerInfo details for specific passenger IDs
 *
 * @param passengerIds - Array of passenger IDs to lookup
 * @param allPassengers - Complete array of all passengers with details
 * @returns Array of PassengerInfo for the specified IDs
 *
 * @example
 * getPassengerDetails([4, 5], allPassengers)
 * // Returns: [
 * //   { id: 4, type: 'adult' },
 * //   { id: 5, type: 'child', age: 4 }
 * // ]
 */
export function getPassengerDetails(
  passengerIds: number[],
  allPassengers: PassengerInfo[]
): PassengerInfo[] {
  return passengerIds
    .map((id) => allPassengers.find((p) => p.id === id))
    .filter((p): p is PassengerInfo => p !== undefined);
}

// ----- Hotel classification (Adult / Child / Infant) -----

/** Player PassengerCode → our 3-way hotelClass. */
const PASSENGER_CODE_TO_CLASS: Record<'Adult' | 'Child' | 'Infant', 'adult' | 'child' | 'infant'> =
  {
    Adult: 'adult',
    Child: 'child',
    Infant: 'infant',
  };

/**
 * Build a passenger-id → hotelClass map from an offer's price breakdown.
 * Player echoes the global passenger ids in `PriceInformation.Passengers`, each carrying
 * a `PassengerCode` (Adult / Child / Infant) — the hotel's classification for this offer.
 */
function buildPassengerClassMap(
  product: PlayerProductOffer
): Map<number, 'adult' | 'child' | 'infant'> {
  const map = new Map<number, 'adult' | 'child' | 'infant'>();

  const raw = product.PriceInformation?.Passengers?.PriceInformationPassenger;
  if (!raw) return map;

  const list = Array.isArray(raw) ? raw : [raw];
  for (const entry of list) {
    map.set(entry.Id, PASSENGER_CODE_TO_CLASS[entry.PassengerCode]);
  }

  return map;
}

/**
 * Set `hotelClass` on each passenger from the classification map (by id).
 * Returns a new array; passengers with no mapped class are left unchanged.
 */
function enrichPassengersWithClassification(
  passengers: PassengerInfo[],
  classMap: Map<number, 'adult' | 'child' | 'infant'>
): PassengerInfo[] {
  return passengers.map((passenger) => {
    const hotelClass = classMap.get(passenger.id);
    return hotelClass ? { ...passenger, hotelClass } : passenger;
  });
}

/**
 * Detect if response contains multi-room offers
 * Multi-room = at least 2 distinct groups of passenger IDs
 *
 * @param offers - Mapped offers to check
 * @returns true if multi-room, false if single-room
 */
export function isMultiRoomResponse(offers: MappedOffer[]): boolean {
  if (offers.length === 0) return false;

  // Group by passenger ID sets
  const uniquePassengerGroups = new Set<string>();

  offers.forEach((offer) => {
    if (offer.passengerIds.length > 0) {
      const key = offer.passengerIds.sort((a, b) => a - b).join(',');
      uniquePassengerGroups.add(key);
    }
  });

  return uniquePassengerGroups.size >= 2;
}

// ----- Room Combination Logic -----

/**
 * Create combinations for single-room scenario
 * Each offer becomes a combination with 1 product
 */
function createSingleRoomCombinations(roomGroups: Map<string, MappedOffer[]>): RoomCombination[] {
  const [, singleRoomOffers] = Array.from(roomGroups.entries())[0];

  return singleRoomOffers.map((offer) => ({
    products: [offer],
    totalPrice: offer.price,
    currency: offer.currency,
    boardCode: offer.boardCode,
    boardType: offer.boardType,
    boardName: offer.boardName,
    startDate: offer.startDate,
    endDate: offer.endDate,
    roomDescriptions: [offer.roomDescription],
    roomCodes: [offer.roomCode],
    passengerCounts: [offer.passengerIds.length || 0],
    availability: offer.availability,
    discount: offer.discount,
    discountReason: offer.discountReason,
    oldPrice: offer.oldPrice,
    allPassengers: offer.passengers,
  }));
}

/**
 * Create combinations for multi-room scenario
 * Pairs products with same meal plan from different rooms
 */
function createMultiRoomCombinations(roomGroups: Map<string, MappedOffer[]>): RoomCombination[] {
  // Step 1: Group each room's offers by date + room code + meal plan
  const roomOffersByKey = new Map<string, Map<string, MappedOffer>>();

  for (const [passengerKey, roomOffers] of roomGroups.entries()) {
    const offersByKey = new Map<string, MappedOffer>();

    for (const offer of roomOffers) {
      // Key: date + room + meal plan
      const offerKey = `${offer.startDate}|${offer.endDate}|${offer.roomCode}|${offer.boardCode}`;

      // Keep cheapest offer per key
      const existing = offersByKey.get(offerKey);
      if (!existing || offer.price < existing.price) {
        offersByKey.set(offerKey, offer);
      }
    }

    roomOffersByKey.set(passengerKey, offersByKey);
  }

  // Step 2: Find matching combinations across rooms
  const combinations: RoomCombination[] = [];
  const roomGroupsArray = Array.from(roomOffersByKey.entries());

  if (roomGroupsArray.length < 2) {
    return [];
  }

  // For each offer in first room, find matching offers in other rooms
  const [, firstRoomOffers] = roomGroupsArray[0];

  for (const [offerKey, firstOffer] of firstRoomOffers.entries()) {
    const [startDate, endDate, , boardCode] = offerKey.split('|');

    // Try to find matching products in all other rooms
    const matchingProducts: MappedOffer[] = [firstOffer];
    let allRoomsMatched = true;

    for (let i = 1; i < roomGroupsArray.length; i++) {
      const [, otherRoomOffers] = roomGroupsArray[i];

      // Find product with matching date and meal plan (any room code)
      let matchFound = false;
      for (const [otherKey, otherOffer] of otherRoomOffers.entries()) {
        const [otherStart, otherEnd, , otherBoard] = otherKey.split('|');

        if (otherStart === startDate && otherEnd === endDate && otherBoard === boardCode) {
          matchingProducts.push(otherOffer);
          matchFound = true;
          break;
        }
      }

      if (!matchFound) {
        allRoomsMatched = false;
        break;
      }
    }

    // If we found matching products for all rooms, create combination
    if (allRoomsMatched && matchingProducts.length === roomGroupsArray.length) {
      // Align products with the searched room order: each product's original Player
      // passengerIds identify its searched room (room 1 = ids 1..n1, room 2 follows, ...).
      // The Player may return rooms in any order (and cross-assign groups to rooms by
      // capacity), while the checkout URL contract pairs roomCodes[i] with the i-th pax
      // room block. Math.min because ids within a room may arrive shuffled (e.g. 1,2,4,3).
      matchingProducts.sort((a, b) => Math.min(...a.passengerIds) - Math.min(...b.passengerIds));

      const totalPrice = matchingProducts.reduce((sum, p) => sum + p.price, 0);
      const currency = firstOffer.currency;

      let oldPrice: number | undefined;
      if (matchingProducts.some((p) => p.oldPrice)) {
        oldPrice = matchingProducts.reduce((sum, p) => sum + (p.oldPrice || p.price), 0);
      }

      let discount: number | undefined;
      if (oldPrice && oldPrice > totalPrice) {
        discount = Math.round(((oldPrice - totalPrice) / oldPrice) * 100);
      }

      const allPassengers = matchingProducts
        .flatMap((p) => p.passengers)
        .sort((a, b) => a.id - b.id);

      combinations.push({
        products: matchingProducts,
        totalPrice,
        currency,
        boardCode: firstOffer.boardCode,
        boardType: firstOffer.boardType,
        boardName: firstOffer.boardName,
        startDate: firstOffer.startDate,
        endDate: firstOffer.endDate,
        roomDescriptions: matchingProducts.map((p) => p.roomDescription),
        roomCodes: matchingProducts.map((p) => p.roomCode),
        passengerCounts: matchingProducts.map((p) => p.passengerIds.length || 0),
        availability: 'unknown',
        discount,
        discountReason: matchingProducts.find((p) => p.discountReason)?.discountReason,
        oldPrice,
        allPassengers,
      });
    }
  }

  return combinations.sort((a, b) => a.totalPrice - b.totalPrice);
}

/**
 * Create room combinations from individual room products
 *
 * Single-room: Each offer becomes a combination with 1 product
 * Multi-room: Pairs products with different PassengerIds but same meal plan
 *
 * @param offers - All mapped offers from API (individual room products)
 * @returns Array of RoomCombination objects
 *
 * @example Single-room
 * Input: [
 *   { offerId: 1, passengerIds: [1,2,3], boardCode: 'F', price: 1000 },
 *   { offerId: 2, passengerIds: [1,2,3], boardCode: 'H', price: 1200 }
 * ]
 * Output: [
 *   { products: [offer1], totalPrice: 1000, boardCode: 'F' },
 *   { products: [offer2], totalPrice: 1200, boardCode: 'H' }
 * ]
 *
 * @example Multi-room
 * Input: [
 *   { offerId: 1, passengerIds: [4,5], boardCode: 'F', price: 630 },
 *   { offerId: 15, passengerIds: [1,2,3], boardCode: 'F', price: 1758 },
 *   { offerId: 8, passengerIds: [4,5], boardCode: 'H', price: 938 },
 *   { offerId: 22, passengerIds: [1,2,3], boardCode: 'H', price: 2682 }
 * ]
 * Output: [
 *   { products: [offer15, offer1], totalPrice: 2388, boardCode: 'F' },
 *   { products: [offer22, offer8], totalPrice: 3620, boardCode: 'H' }
 * ]
 */
export function createRoomCombinations(offers: MappedOffer[]): RoomCombination[] {
  if (offers.length === 0) return [];

  // Step 1: Group offers by PassengerIds (identify separate rooms)
  const roomGroups = new Map<string, MappedOffer[]>();

  for (const offer of offers) {
    const passengerKey =
      offer.passengerIds.length > 0 ? offer.passengerIds.sort((a, b) => a - b).join(',') : 'all'; // Fallback for offers without passenger IDs

    const existing = roomGroups.get(passengerKey) || [];
    existing.push(offer);
    roomGroups.set(passengerKey, existing);
  }

  if (roomGroups.size === 1) {
    // Single-room: create combinations with 1 product each
    return createSingleRoomCombinations(roomGroups);
  } else {
    // Multi-room: create combinations by pairing products
    return createMultiRoomCombinations(roomGroups);
  }
}

/**
 * A combination is fixed-bookable when none of its products requires request booking
 * (mirrors applyRequestBookingOverride in OfferPage.vue: any requestBooking product
 * makes the whole combination on-request)
 */
export function isFixedBookingCombination(combo: RoomCombination): boolean {
  return combo.products.every((p) => !p.requestBooking);
}

/** Fixed-bookable combinations first, then totalPrice ascending */
export function compareCombinationsFixedFirst(a: RoomCombination, b: RoomCombination): number {
  const fixedDiff = Number(isFixedBookingCombination(b)) - Number(isFixedBookingCombination(a));
  return fixedDiff !== 0 ? fixedDiff : a.totalPrice - b.totalPrice;
}

/**
 * Ranking inputs for the offer list.
 *
 * Both are optional, and with neither of them the ordering is byte-for-byte the
 * one this page has always used.
 */
export interface OfferGroupRanking {
  /** Length the request asked for; groups closer to it rank higher. */
  selectedDuration?: number;
  /**
   * Travel start date of the offer a search-result card was clicked on. Ranks the
   * group holding that exact offer to the top, so the price the user clicked is
   * the price they land on.
   */
  anchorStartDate?: string;
}

/**
 * Read the calendar day out of an API or URL date, as a local-midnight `Date`.
 *
 * The two sources disagree on shape: the API reports `2026-10-16T00:00:00`, which
 * `new Date()` reads as local time, while a URL parameter is a bare `2026-10-16`,
 * which it reads as UTC. Feeding both to `new Date()` and subtracting would put
 * them a day apart in any negative-offset timezone, so only the date part is used
 * and the components are applied locally.
 */
function parseCalendarDate(value: string | undefined): Date | null {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.slice(0, 10).split('-').map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
}

function nightsBetween(start: string, end: string): number | undefined {
  const from = parseCalendarDate(start);
  const to = parseCalendarDate(end);

  if (!from || !to) {
    return undefined;
  }

  const nights = daysBetween(from, to);

  return Number.isFinite(nights) && nights > 0 ? nights : undefined;
}

function durationDistance(group: RoomCombinationGroup, selectedDuration?: number): number {
  if (selectedDuration === undefined) {
    return 0;
  }
  return group.nights === undefined ? Infinity : Math.abs(group.nights - selectedDuration);
}

function anchorDistance(group: RoomCombinationGroup, anchorStartDate?: string): number {
  const anchor = parseCalendarDate(anchorStartDate);
  const start = parseCalendarDate(group.startDate);

  if (!anchor) {
    return 0;
  }
  if (!start) {
    return Infinity;
  }

  return Math.abs(daysBetween(anchor, start));
}

function pricePerNight(group: RoomCombinationGroup): number {
  const total = group.cheapestCombination.totalPrice;
  return group.nights ? total / group.nights : total;
}

/**
 * Group order: distance from the requested length, then distance from the clicked
 * offer's departure, then fixed-bookable count descending, then price per night.
 *
 * Length leads because a shorter stay in the same room is always cheaper, so
 * ordering by price alone buries the length the user actually asked for. Price is
 * compared per night because ranking a 6-night stay against a 14-night one on the
 * total is not a comparison of anything.
 *
 * With a single requested length and no anchor the first two keys are constant and
 * the divisor is the same for every group, so the result is today's ordering.
 */
function compareGroups(
  a: RoomCombinationGroup,
  b: RoomCombinationGroup,
  ranking: OfferGroupRanking
): number {
  return (
    durationDistance(a, ranking.selectedDuration) - durationDistance(b, ranking.selectedDuration) ||
    anchorDistance(a, ranking.anchorStartDate) - anchorDistance(b, ranking.anchorStartDate) ||
    b.fixedBookingCount - a.fixedBookingCount ||
    pricePerNight(a) - pricePerNight(b)
  );
}

/**
 * Group room combinations by room types (for display as cards with meal options)
 * Groups combinations with same rooms but different meal plans
 * Works for both single-room and multi-room combinations
 *
 * Group order: see `compareGroups`. Without `ranking` it is fixed-bookable count
 * descending, then cheapest price ascending — exactly as before.
 *
 * @param combinations - Array of room combinations
 * @param ranking - Optional length and departure the list should rank towards
 * @returns Array of RoomCombinationGroup objects
 */
export function groupCombinationsByRoomTypes(
  combinations: RoomCombination[],
  ranking: OfferGroupRanking = {}
): RoomCombinationGroup[] {
  if (combinations.length === 0) return [];

  // Group by date + room codes combination
  const groupMap = new Map<string, RoomCombination[]>();

  for (const combo of combinations) {
    const groupKey = `${combo.startDate}|${combo.endDate}|${combo.roomCodes.sort().join(',')}`;
    const existing = groupMap.get(groupKey) || [];
    existing.push(combo);
    groupMap.set(groupKey, existing);
  }

  const groups: RoomCombinationGroup[] = [];

  for (const [groupKey, combos] of groupMap.entries()) {
    const sorted = [...combos].sort((a, b) => a.totalPrice - b.totalPrice);
    const cheapest = sorted[0];
    const others = sorted.slice(1).sort(compareCombinationsFixedFirst);

    groups.push({
      groupKey,
      startDate: cheapest.startDate,
      endDate: cheapest.endDate,
      roomDescriptions: cheapest.roomDescriptions,
      roomCodes: cheapest.roomCodes,
      passengerCounts: cheapest.passengerCounts,
      cheapestCombination: cheapest,
      otherCombinations: others,
      totalRooms: cheapest.products.length,
      isMultiRoom: cheapest.products.length > 1,
      availability: cheapest.availability,
      fixedBookingCount: combos.filter(isFixedBookingCombination).length,
      nights: nightsBetween(cheapest.startDate, cheapest.endDate),
    });
  }

  return groups.sort((a, b) => compareGroups(a, b, ranking));
}

/**
 * Map offers from API to display-ready structure
 *
 * @param result - Player API response
 * @param roomOccupancy - Room occupancy from search params (required for passenger details)
 * @param ranking - Optional length and departure the list should rank towards
 * @returns Array of room combination groups
 */
export function mapAndGroupOffers(
  result: GetProductOffersResult | null,
  roomOccupancy: RoomOccupancy,
  ranking: OfferGroupRanking = {}
): RoomCombinationGroup[] {
  if (!result) return [];

  const mappedOffers = mapOffersFromApi(result, roomOccupancy);
  if (mappedOffers.length === 0) return [];

  const combinations = createRoomCombinations(mappedOffers);

  return groupCombinationsByRoomTypes(combinations, ranking);
}
